import React, { useMemo, useState } from 'react';
import { ArrowDownToLine, Eye, TrendingUp, TrendingDown, Zap, DollarSign, Repeat, X, Globe, ChevronDown, ShoppingBag, Users, Lightbulb, ArrowRight, MapPin } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
interface StatsTabProps {
  showToast: (msg: string) => void;
  setActiveTab: (tab: string) => void;
}
type Period = 'week' | 'month' | 'quarter' | 'year';
interface PeriodData {
  views: number;
  viewsTrend: number;
  conversion: number;
  convTrend: number;
  avgCheck: number;
  avgCheckTrend: number;
  repeatClients: number;
  repeatTrend: number;
  income: number;
  incomeTrend: number;
  orders: number;
  ordersTrend: number;
}
const PERIOD_DATA: Record<Period, PeriodData> = {
  week: {
    views: 312,
    viewsTrend: 8,
    conversion: 9.1,
    convTrend: 0.5,
    avgCheck: 4900,
    avgCheckTrend: 2,
    repeatClients: 38,
    repeatTrend: 3,
    income: 78400,
    incomeTrend: 12,
    orders: 16,
    ordersTrend: 10
  },
  month: {
    views: 1247,
    viewsTrend: 15,
    conversion: 8.3,
    convTrend: 1.2,
    avgCheck: 4800,
    avgCheckTrend: 0,
    repeatClients: 34,
    repeatTrend: 5,
    income: 307200,
    incomeTrend: 22,
    orders: 64,
    ordersTrend: 18
  },
  quarter: {
    views: 3890,
    viewsTrend: 20,
    conversion: 8.0,
    convTrend: 0.8,
    avgCheck: 4750,
    avgCheckTrend: -1,
    repeatClients: 31,
    repeatTrend: 4,
    income: 890000,
    incomeTrend: 25,
    orders: 187,
    ordersTrend: 22
  },
  year: {
    views: 14200,
    viewsTrend: 35,
    conversion: 7.8,
    convTrend: 1.5,
    avgCheck: 4700,
    avgCheckTrend: 3,
    repeatClients: 29,
    repeatTrend: 8,
    income: 3200000,
    incomeTrend: 40,
    orders: 680,
    ordersTrend: 35
  }
};
const CHART_DATA = [{
  month: 'Май',
  orders: 24,
  revenue: 115000
}, {
  month: 'Июн',
  orders: 32,
  revenue: 154000
}, {
  month: 'Июл',
  orders: 28,
  revenue: 135000
}, {
  month: 'Авг',
  orders: 45,
  revenue: 216000
}, {
  month: 'Сен',
  orders: 52,
  revenue: 250000
}, {
  month: 'Окт',
  orders: 64,
  revenue: 307000
}];
const SOURCES = [{
  name: 'Каталог астрологов',
  percent: 52,
  orders: 33,
  revenue: 159000,
  color: 'bg-indigo-500'
}, {
  name: 'Поиск на платформе',
  percent: 28,
  orders: 18,
  revenue: 86000,
  color: 'bg-sky-400'
}, {
  name: 'Прямая ссылка',
  percent: 12,
  orders: 8,
  revenue: 38000,
  color: 'bg-emerald-400'
}, {
  name: 'Рекомендации',
  percent: 8,
  orders: 5,
  revenue: 24000,
  color: 'bg-amber-400'
}];
const CITIES = [{
  city: 'Москва',
  percent: 35,
  orders: 22,
  revenue: 106000
}, {
  city: 'Санкт-Петербург',
  percent: 18,
  orders: 12,
  revenue: 55000
}, {
  city: 'Казань',
  percent: 8,
  orders: 5,
  revenue: 24000
}, {
  city: 'Новосибирск',
  percent: 7,
  orders: 4,
  revenue: 21000
}, {
  city: 'Екатеринбург',
  percent: 5,
  orders: 3,
  revenue: 15000
}];
const TOP_SERVICES = [{
  name: 'Натальная карта (Видео)',
  orders: 42,
  conv: 12.4,
  revenue: 210000
}, {
  name: 'Соляр на год (Чат)',
  orders: 28,
  conv: 9.8,
  revenue: 112000
}, {
  name: 'Синастрия',
  orders: 15,
  conv: 5.2,
  revenue: 90000
}, {
  name: 'Экспресс-консультация',
  orders: 12,
  conv: 7.1,
  revenue: 36000
}];
const RECOMMENDATIONS = [{
  text: '«Натальная карта» — ваша самая популярная услуга. Попробуйте поднять цену на 10-15%.',
  action: 'Перейти к услугам',
  tab: 'services'
}, {
  text: 'Конверсия «Синастрии» ниже среднего (5.2%). Улучшите описание и добавьте примеры.',
  action: 'Редактировать',
  tab: 'services'
}, {
  text: '34% клиентов возвращаются. Предложите им скидку на повторный заказ.',
  action: 'Создать акцию',
  tab: 'promotion'
}];
export function StatsTab({
  showToast,
  setActiveTab
}: StatsTabProps) {
  const [period, setPeriod] = useState<Period>('month');
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [selectedBarIdx, setSelectedBarIdx] = useState<number | null>(null);
  const [expandedSource, setExpandedSource] = useState<number | null>(null);
  const [expandedCity, setExpandedCity] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<'orders' | 'conv' | 'revenue'>('revenue');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const data = PERIOD_DATA[period];
  const maxOrders = Math.max(...CHART_DATA.map(d => d.orders));
  const formatMoney = (n: number) => new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0
  }).format(n);
  const sortedServices = useMemo(() => {
    return [...TOP_SERVICES].sort((a, b) => {
      const aVal = sortBy === 'orders' ? a.orders : sortBy === 'conv' ? a.conv : a.revenue;
      const bVal = sortBy === 'orders' ? b.orders : sortBy === 'conv' ? b.conv : b.revenue;
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    });
  }, [sortBy, sortDir]);
  const handleSort = (col: 'orders' | 'conv' | 'revenue') => {
    if (sortBy === col) {
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };
  const TrendBadge = ({
    value
  }: {
    value: number;
  }) => {
    if (value === 0) return <span className="text-xs text-slate-500 font-medium" data-id="element-15295">
          Без изменений
        </span>;
    const isUp = value > 0;
    return <span className={`text-xs font-medium flex items-center gap-0.5 ${isUp ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-15296">
        {isUp ? <TrendingUp className="w-3 h-3" data-id="element-15297" /> : <TrendingDown className="w-3 h-3" data-id="element-15298" />}
        {isUp ? '+' : ''}
        {value}%
      </span>;
  };
  return <div className="space-y-6" data-id="element-15299">
      {/* Header + Period */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-15300">
        <h2 className="text-2xl font-bold text-slate-900" data-id="element-15301">Аналитика</h2>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto" data-id="element-15302">
          <div className="flex bg-slate-100 p-1 rounded-lg overflow-x-auto" data-id="element-15303">
            {(['week', 'month', 'quarter', 'year'] as Period[]).map(p => <button key={p} onClick={() => setPeriod(p)} className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors flex-1 sm:flex-none text-center ${period === p ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-15304">
                {{
              week: 'Неделя',
              month: 'Месяц',
              quarter: 'Квартал',
              year: 'Год'
            }[p]}
              </button>)}
          </div>
          <Button variant="outline" icon={<ArrowDownToLine className="w-4 h-4" data-id="element-15306" />} onClick={() => showToast('Отчёт скачивается...')} className="w-full sm:w-auto" data-id="element-15305">
            Скачать
          </Button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-id="element-15307">
        {[{
        key: 'views',
        label: 'Просмотры',
        value: data.views.toLocaleString('ru-RU'),
        trend: data.viewsTrend,
        icon: Eye,
        color: 'text-slate-400'
      }, {
        key: 'conversion',
        label: 'Конверсия',
        value: `${data.conversion}%`,
        trend: data.convTrend,
        icon: Zap,
        color: 'text-indigo-400'
      }, {
        key: 'avgCheck',
        label: 'Средний чек',
        value: formatMoney(data.avgCheck),
        trend: data.avgCheckTrend,
        icon: DollarSign,
        color: 'text-emerald-400'
      }, {
        key: 'repeat',
        label: 'Повторные',
        value: `${data.repeatClients}%`,
        trend: data.repeatTrend,
        icon: Repeat,
        color: 'text-amber-400'
      }, {
        key: 'income',
        label: 'Доход',
        value: formatMoney(data.income),
        trend: data.incomeTrend,
        icon: DollarSign,
        color: 'text-emerald-500',
        highlight: true
      }, {
        key: 'orders',
        label: 'Заказы',
        value: data.orders.toString(),
        trend: data.ordersTrend,
        icon: ShoppingBag,
        color: 'text-indigo-500',
        highlight: true
      }].map(card => <Card key={card.key} padding="sm" className={`cursor-pointer transition-all hover:border-indigo-300 ${expandedCard === card.key ? 'border-indigo-500 ring-1 ring-indigo-500' : ''} ${card.highlight ? 'bg-slate-50' : ''}`} onClick={() => setExpandedCard(expandedCard === card.key ? null : card.key)} data-id="element-15308">
            <div className="flex justify-between items-start mb-1" data-id="element-15309">
              <div className="text-xs text-slate-500 font-medium" data-id="element-15310">
                {card.label}
              </div>
              <card.icon className={`w-4 h-4 ${card.color}`} data-id="element-15311" />
            </div>
            <div className="text-xl font-bold text-slate-900 mb-1" data-id="element-15312">
              {card.value}
            </div>
            <TrendBadge value={card.trend} data-id="element-15313" />
          </Card>)}
      </div>

      {/* Expanded Detail */}
      {expandedCard && <div className="animate-in fade-in slide-in-from-top-2" data-id="element-15314">
          <Card padding="md" className="bg-indigo-50/30 border-indigo-100 relative" data-id="element-15315">
            <button onClick={() => setExpandedCard(null)} className="absolute top-3 right-3 p-1 text-slate-400 hover:text-slate-600 rounded-full hover:bg-white transition-colors" data-id="element-15316">
              <X className="w-4 h-4" data-id="element-15317" />
            </button>
            <h4 className="font-bold text-slate-900 mb-3 text-sm" data-id="element-15318">
              Детализация:{' '}
              {{
            views: 'Просмотры',
            conversion: 'Конверсия',
            avgCheck: 'Средний чек',
            repeat: 'Повторные клиенты',
            income: 'Доход',
            orders: 'Заказы'
          }[expandedCard]}
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-15319">
              {expandedCard === 'views' && <>
                  <div data-id="element-15320">
                    <div className="text-xs text-slate-500" data-id="element-15321">Сегодня</div>
                    <div className="font-bold text-slate-900" data-id="element-15322">42</div>
                  </div>
                  <div data-id="element-15323">
                    <div className="text-xs text-slate-500" data-id="element-15324">Вчера</div>
                    <div className="font-bold text-slate-900" data-id="element-15325">38</div>
                  </div>
                  <div data-id="element-15326">
                    <div className="text-xs text-slate-500" data-id="element-15327">Из каталога</div>
                    <div className="font-bold text-slate-900" data-id="element-15328">65%</div>
                  </div>
                  <div data-id="element-15329">
                    <div className="text-xs text-slate-500" data-id="element-15330">Из поиска</div>
                    <div className="font-bold text-slate-900" data-id="element-15331">28%</div>
                  </div>
                </>}
              {expandedCard === 'conversion' && <>
                  <div data-id="element-15332">
                    <div className="text-xs text-slate-500" data-id="element-15333">Просмотры</div>
                    <div className="font-bold text-slate-900" data-id="element-15334">
                      {data.views.toLocaleString('ru-RU')}
                    </div>
                  </div>
                  <div data-id="element-15335">
                    <div className="text-xs text-slate-500" data-id="element-15336">Контакты</div>
                    <div className="font-bold text-slate-900" data-id="element-15337">
                      {Math.round(data.views * 0.24)}
                    </div>
                  </div>
                  <div data-id="element-15338">
                    <div className="text-xs text-slate-500" data-id="element-15339">Заказы</div>
                    <div className="font-bold text-indigo-600" data-id="element-15340">
                      {data.orders}
                    </div>
                  </div>
                  <div data-id="element-15341">
                    <div className="text-xs text-slate-500" data-id="element-15342">Итоговая</div>
                    <div className="font-bold text-emerald-600" data-id="element-15343">
                      {data.conversion}%
                    </div>
                  </div>
                </>}
              {expandedCard === 'avgCheck' && <>
                  <div data-id="element-15344">
                    <div className="text-xs text-slate-500" data-id="element-15345">До 3 000 ₽</div>
                    <div className="font-bold text-slate-900" data-id="element-15346">20%</div>
                  </div>
                  <div data-id="element-15347">
                    <div className="text-xs text-slate-500" data-id="element-15348">3-5 000 ₽</div>
                    <div className="font-bold text-slate-900" data-id="element-15349">55%</div>
                  </div>
                  <div data-id="element-15350">
                    <div className="text-xs text-slate-500" data-id="element-15351">Более 5 000 ₽</div>
                    <div className="font-bold text-slate-900" data-id="element-15352">25%</div>
                  </div>
                  <div data-id="element-15353">
                    <div className="text-xs text-slate-500" data-id="element-15354">Макс. чек</div>
                    <div className="font-bold text-emerald-600" data-id="element-15355">15 000 ₽</div>
                  </div>
                </>}
              {expandedCard === 'repeat' && <>
                  <div data-id="element-15356">
                    <div className="text-xs text-slate-500" data-id="element-15357">Всего клиентов</div>
                    <div className="font-bold text-slate-900" data-id="element-15358">104</div>
                  </div>
                  <div data-id="element-15359">
                    <div className="text-xs text-slate-500" data-id="element-15360">Новые</div>
                    <div className="font-bold text-slate-900" data-id="element-15361">69 (66%)</div>
                  </div>
                  <div data-id="element-15362">
                    <div className="text-xs text-slate-500" data-id="element-15363">Повторные</div>
                    <div className="font-bold text-indigo-600" data-id="element-15364">35 (34%)</div>
                  </div>
                  <div data-id="element-15365">
                    <div className="text-xs text-slate-500" data-id="element-15366">3+ заказа</div>
                    <div className="font-bold text-emerald-600" data-id="element-15367">12 чел.</div>
                  </div>
                </>}
              {expandedCard === 'income' && <>
                  <div data-id="element-15368">
                    <div className="text-xs text-slate-500" data-id="element-15369">Услуги</div>
                    <div className="font-bold text-slate-900" data-id="element-15370">
                      {formatMoney(data.income * 0.85)}
                    </div>
                  </div>
                  <div data-id="element-15371">
                    <div className="text-xs text-slate-500" data-id="element-15372">Товары</div>
                    <div className="font-bold text-slate-900" data-id="element-15373">
                      {formatMoney(data.income * 0.15)}
                    </div>
                  </div>
                  <div data-id="element-15374">
                    <div className="text-xs text-slate-500" data-id="element-15375">Комиссия (20%)</div>
                    <div className="font-bold text-rose-600" data-id="element-15376">
                      -{formatMoney(data.income * 0.2)}
                    </div>
                  </div>
                  <div data-id="element-15377">
                    <div className="text-xs text-slate-500" data-id="element-15378">На руки</div>
                    <div className="font-bold text-emerald-600" data-id="element-15379">
                      {formatMoney(data.income * 0.8)}
                    </div>
                  </div>
                </>}
              {expandedCard === 'orders' && <>
                  <div data-id="element-15380">
                    <div className="text-xs text-slate-500" data-id="element-15381">Завершено</div>
                    <div className="font-bold text-emerald-600" data-id="element-15382">
                      {Math.round(data.orders * 0.85)}
                    </div>
                  </div>
                  <div data-id="element-15383">
                    <div className="text-xs text-slate-500" data-id="element-15384">В работе</div>
                    <div className="font-bold text-amber-600" data-id="element-15385">
                      {Math.round(data.orders * 0.1)}
                    </div>
                  </div>
                  <div data-id="element-15386">
                    <div className="text-xs text-slate-500" data-id="element-15387">Отменено</div>
                    <div className="font-bold text-rose-600" data-id="element-15388">
                      {Math.round(data.orders * 0.05)}
                    </div>
                  </div>
                  <div data-id="element-15389">
                    <div className="text-xs text-slate-500" data-id="element-15390">Средний/день</div>
                    <div className="font-bold text-slate-900" data-id="element-15391">
                      {(data.orders / (period === 'week' ? 7 : period === 'month' ? 30 : period === 'quarter' ? 90 : 365)).toFixed(1)}
                    </div>
                  </div>
                </>}
            </div>
          </Card>
        </div>}

      {/* Orders Chart */}
      <Card padding="md" data-id="element-15392">
        <h3 className="font-bold text-slate-900 mb-5" data-id="element-15393">Заказы по месяцам</h3>
        <div className="h-48 flex items-end justify-between gap-2 sm:gap-4 border-b border-slate-100 pb-2" data-id="element-15394">
          {CHART_DATA.map((bar, i) => {
          const heightPct = bar.orders / maxOrders * 100;
          const isSelected = selectedBarIdx === i;
          return <div key={i} className="flex flex-col items-center flex-1 group cursor-pointer" onClick={() => setSelectedBarIdx(isSelected ? null : i)} data-id="element-15395">
                <div className={`text-xs mb-1.5 font-bold transition-opacity ${isSelected ? 'opacity-100 text-indigo-600' : 'opacity-0 group-hover:opacity-100 text-slate-400'}`} data-id="element-15396">
                  {bar.orders}
                </div>
                <div className={`w-full max-w-[44px] rounded-t-md transition-colors ${isSelected ? 'bg-indigo-600' : 'bg-indigo-200 group-hover:bg-indigo-400'}`} style={{
              height: `${heightPct}%`
            }} data-id="element-15397" />
                <div className={`text-xs mt-2 font-medium ${isSelected ? 'text-indigo-600' : 'text-slate-500'}`} data-id="element-15398">
                  {bar.month}
                </div>
              </div>;
        })}
        </div>

        {selectedBarIdx !== null && <div className="mt-4 p-3 bg-slate-50 rounded-xl border border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 animate-in fade-in slide-in-from-top-2" data-id="element-15399">
            <div className="flex flex-wrap gap-4 text-sm" data-id="element-15400">
              <span className="text-slate-600" data-id="element-15401">
                Заказов:{' '}
                <strong className="text-slate-900" data-id="element-15402">
                  {CHART_DATA[selectedBarIdx].orders}
                </strong>
              </span>
              <span className="text-slate-600" data-id="element-15403">
                Выручка:{' '}
                <strong className="text-emerald-600" data-id="element-15404">
                  {formatMoney(CHART_DATA[selectedBarIdx].revenue)}
                </strong>
              </span>
            </div>
            <Button variant="outline" size="sm" onClick={() => {
          showToast(`Заказы за ${CHART_DATA[selectedBarIdx!].month}`);
          setActiveTab('orders');
        }} data-id="element-15405">
              К заказам <ArrowRight className="w-3.5 h-3.5 ml-1" data-id="element-15406" />
            </Button>
          </div>}
      </Card>

      {/* Sources + Geography */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-15407">
        {/* Sources */}
        <Card padding="md" data-id="element-15408">
          <h3 className="font-bold text-slate-900 mb-5" data-id="element-15409">Источники клиентов</h3>
          <div className="space-y-4" data-id="element-15410">
            {SOURCES.map((src, i) => <div key={i} data-id="element-15411">
                <div className="cursor-pointer group" onClick={() => setExpandedSource(expandedSource === i ? null : i)} data-id="element-15412">
                  <div className="flex justify-between text-sm mb-1.5" data-id="element-15413">
                    <span className="font-medium text-slate-700 group-hover:text-indigo-600 transition-colors" data-id="element-15414">
                      {src.name}
                    </span>
                    <span className="font-bold text-slate-900" data-id="element-15415">
                      {src.percent}%
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden" data-id="element-15416">
                    <div className={`h-full ${src.color} rounded-full`} style={{
                  width: `${src.percent}%`
                }} data-id="element-15417" />
                  </div>
                </div>
                {expandedSource === i && <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-100 flex gap-6 text-sm animate-in fade-in slide-in-from-top-2" data-id="element-15418">
                    <div data-id="element-15419">
                      <span className="text-slate-500" data-id="element-15420">Заказов:</span>{' '}
                      <strong className="text-slate-900" data-id="element-15421">{src.orders}</strong>
                    </div>
                    <div data-id="element-15422">
                      <span className="text-slate-500" data-id="element-15423">Доход:</span>{' '}
                      <strong className="text-emerald-600" data-id="element-15424">
                        {formatMoney(src.revenue)}
                      </strong>
                    </div>
                  </div>}
              </div>)}
          </div>
        </Card>

        {/* Geography */}
        <Card padding="md" data-id="element-15425">
          <h3 className="font-bold text-slate-900 mb-5 flex items-center gap-2" data-id="element-15426">
            <Globe className="w-5 h-5 text-slate-400" data-id="element-15427" /> География
          </h3>
          <div className="space-y-3" data-id="element-15428">
            {CITIES.map((geo, i) => <div key={i} data-id="element-15429">
                <div className="flex items-center gap-3 cursor-pointer group" onClick={() => setExpandedCity(expandedCity === i ? null : i)} data-id="element-15430">
                  <div className="w-5 text-center text-xs font-bold text-slate-400" data-id="element-15431">
                    {i + 1}
                  </div>
                  <div className="flex-1" data-id="element-15432">
                    <div className="flex justify-between text-sm mb-1" data-id="element-15433">
                      <span className="font-medium text-slate-700 group-hover:text-indigo-600 transition-colors flex items-center gap-1" data-id="element-15434">
                        <MapPin className="w-3 h-3" data-id="element-15435" /> {geo.city}
                      </span>
                      <span className="font-bold text-slate-900" data-id="element-15436">
                        {geo.percent}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5" data-id="element-15437">
                      <div className="bg-slate-400 h-full rounded-full group-hover:bg-indigo-500 transition-colors" style={{
                    width: `${geo.percent}%`
                  }} data-id="element-15438" />
                    </div>
                  </div>
                </div>
                {expandedCity === i && <div className="ml-8 mt-2 p-3 bg-slate-50 rounded-lg border border-slate-100 flex gap-6 text-sm animate-in fade-in slide-in-from-top-2" data-id="element-15439">
                    <div data-id="element-15440">
                      <span className="text-slate-500" data-id="element-15441">Заказов:</span>{' '}
                      <strong className="text-slate-900" data-id="element-15442">{geo.orders}</strong>
                    </div>
                    <div data-id="element-15443">
                      <span className="text-slate-500" data-id="element-15444">Доход:</span>{' '}
                      <strong className="text-emerald-600" data-id="element-15445">
                        {formatMoney(geo.revenue)}
                      </strong>
                    </div>
                  </div>}
              </div>)}
            <div className="pt-2 mt-1 border-t border-slate-100 text-sm text-slate-500 text-center cursor-pointer hover:text-indigo-600 transition-colors" onClick={() => showToast('Все города')} data-id="element-15446">
              Другие города: 27%
            </div>
          </div>
        </Card>
      </div>

      {/* Top Services */}
      <Card padding="none" className="overflow-hidden" data-id="element-15447">
        <div className="p-4 sm:p-5 border-b border-slate-100" data-id="element-15448">
          <h3 className="font-bold text-slate-900" data-id="element-15449">Топ услуги</h3>
        </div>

        {/* Desktop Table */}
        <div className="hidden sm:block overflow-x-auto" data-id="element-15450">
          <table className="w-full text-left text-sm" data-id="element-15451">
            <thead className="bg-slate-50/50 text-slate-500 border-b border-slate-100" data-id="element-15452">
              <tr data-id="element-15453">
                <th className="px-5 py-3 font-semibold" data-id="element-15454">Услуга</th>
                <th className="px-5 py-3 font-semibold cursor-pointer hover:text-slate-900" onClick={() => handleSort('orders')} data-id="element-15455">
                  <span className="flex items-center gap-1" data-id="element-15456">
                    Заказы{' '}
                    <ChevronDown className={`w-3 h-3 transition-transform ${sortBy === 'orders' ? 'text-indigo-600' : ''} ${sortBy === 'orders' && sortDir === 'asc' ? 'rotate-180' : ''}`} data-id="element-15457" />
                  </span>
                </th>
                <th className="px-5 py-3 font-semibold cursor-pointer hover:text-slate-900" onClick={() => handleSort('conv')} data-id="element-15458">
                  <span className="flex items-center gap-1" data-id="element-15459">
                    Конверсия{' '}
                    <ChevronDown className={`w-3 h-3 transition-transform ${sortBy === 'conv' ? 'text-indigo-600' : ''} ${sortBy === 'conv' && sortDir === 'asc' ? 'rotate-180' : ''}`} data-id="element-15460" />
                  </span>
                </th>
                <th className="px-5 py-3 font-semibold text-right cursor-pointer hover:text-slate-900" onClick={() => handleSort('revenue')} data-id="element-15461">
                  <span className="flex items-center justify-end gap-1" data-id="element-15462">
                    Доход{' '}
                    <ChevronDown className={`w-3 h-3 transition-transform ${sortBy === 'revenue' ? 'text-indigo-600' : ''} ${sortBy === 'revenue' && sortDir === 'asc' ? 'rotate-180' : ''}`} data-id="element-15463" />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-15464">
              {sortedServices.map((svc, i) => <tr key={i} className="hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => {
              showToast(`Заказы: ${svc.name}`);
              setActiveTab('orders');
            }} data-id="element-15465">
                  <td className="px-5 py-3 font-medium text-slate-900" data-id="element-15466">
                    {svc.name}
                  </td>
                  <td className="px-5 py-3 text-slate-600" data-id="element-15467">{svc.orders}</td>
                  <td className="px-5 py-3 text-emerald-600" data-id="element-15468">{svc.conv}%</td>
                  <td className="px-5 py-3 text-right font-medium text-slate-900" data-id="element-15469">
                    {formatMoney(svc.revenue)}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="sm:hidden divide-y divide-slate-100" data-id="element-15470">
          {sortedServices.map((svc, i) => <div key={i} className="p-4 cursor-pointer" onClick={() => {
          showToast(`Заказы: ${svc.name}`);
          setActiveTab('orders');
        }} data-id="element-15471">
              <div className="font-bold text-slate-900 text-sm mb-2" data-id="element-15472">
                {svc.name}
              </div>
              <div className="flex justify-between text-sm" data-id="element-15473">
                <span className="text-slate-500" data-id="element-15474">
                  Заказов:{' '}
                  <strong className="text-slate-900" data-id="element-15475">{svc.orders}</strong>
                </span>
                <span className="text-slate-500" data-id="element-15476">
                  Конв:{' '}
                  <strong className="text-emerald-600" data-id="element-15477">{svc.conv}%</strong>
                </span>
                <span className="font-bold text-slate-900" data-id="element-15478">
                  {formatMoney(svc.revenue)}
                </span>
              </div>
            </div>)}
        </div>
      </Card>

      {/* Recommendations */}
      <Card padding="md" data-id="element-15479">
        <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-15480">
          <Lightbulb className="w-5 h-5 text-amber-500" data-id="element-15481" /> Рекомендации
        </h3>
        <div className="space-y-3" data-id="element-15482">
          {RECOMMENDATIONS.map((rec, i) => <div key={i} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-amber-50/50 border border-amber-100 rounded-xl" data-id="element-15483">
              <p className="text-sm text-slate-700 flex-1" data-id="element-15484">{rec.text}</p>
              <Button variant="ghost" size="sm" className="text-indigo-600 hover:bg-indigo-50 shrink-0" onClick={() => {
            showToast(rec.action);
            setActiveTab(rec.tab);
          }} data-id="element-15485">
                {rec.action} <ArrowRight className="w-3.5 h-3.5 ml-1" data-id="element-15486" />
              </Button>
            </div>)}
        </div>
      </Card>
    </div>;
}